/**
 * Typed client for the USER TASKLIST ("Pendências") gateway methods
 * (`user-tasklist-lexide-handoff-2026-07-20.md` §4, FROZEN contract).
 *
 * One per-user pending-work list the lawyer and the concierge both work.
 * The runtime store is the single source of truth; this is one of two equal
 * surfaces (the other is chat via `lx_tasklist`) over the same store.
 *
 * LIVE since `[M:tasklist-api]` landed 2026-07-21 (handoff §5; shas 8f5e4933 /
 * f7e239e2 / 7da665d7 / c6ab2521). There is deliberately NO local stand-in: the
 * build-phase fixture backend that used to sit here re-implemented server
 * behaviour, and every place it drifted became a defect the tests could not
 * see — it masked both a non-canonical sort order and a filter-shape violation
 * before they were caught. A runtime that does not serve `tasklist.*` is
 * handled honestly by `useTasklistGate` (the section hides), not by pretending.
 */
import { useGatewayStore } from "@/stores/useGatewayStore";
import {
  compareTasks,
  localToday,
  TASKLIST_FIXTURE,
  type TaskDetailResult,
  type TaskPatch,
  type TaskPriority,
  type TaskRow,
  type TasklistFilters,
  type UpdateRow,
} from "@lexiun/tasklist-core";

// The wire shapes live in the shared package — the SAME definitions LexPortal
// reads. Re-exported here so every component keeps importing them from the
// module that owns the transport, and none of them has to know the package
// exists.
export type {
  TaskStatus,
  TaskPriority,
  UpdateRow,
  TaskRow,
  TasklistFilters,
  TaskDetailResult,
  TaskPatch,
} from "@lexiun/tasklist-core";
export { TERMINAL_STATUSES } from "@lexiun/tasklist-core";

function rpc() {
  const client = useGatewayStore.getState().rpc;
  if (!client) throw new Error("gateway not connected");
  return client;
}

/** Cast typed params to the rpc.call bag shape (mirrors gateway-rpc's `p()`). */
const asParams = (o: unknown): Record<string, unknown> => o as Record<string, unknown>;

export function listTasks(
  filters: TasklistFilters = {},
): Promise<{ tasks: TaskRow[] }> {
  return rpc().call<{ tasks: TaskRow[] }>("tasklist.list", asParams(filters));
}

export function getTask(
  taskId: string,
  opts: { historyLimit?: number; historyBefore?: string } = {},
): Promise<TaskDetailResult> {
  return rpc().call<TaskDetailResult>("tasklist.get", asParams({ taskId, ...opts }));
}

export function addTask(input: {
  title: string;
  detail?: string;
  priority?: TaskPriority;
  caseRefs?: string[];
  dueDate?: string | null;
  recurrenceOf?: string;
}): Promise<{ task: TaskRow }> {
  return rpc().call<{ task: TaskRow }>("tasklist.add", asParams(input));
}

export function updateTask(
  taskId: string,
  patch: TaskPatch,
  note?: string,
): Promise<{ task: TaskRow }> {
  return rpc().call<{ task: TaskRow }>("tasklist.update", asParams({ taskId, patch, note }));
}

export function noteTask(taskId: string, note: string): Promise<{ task: TaskRow }> {
  return rpc().call<{ task: TaskRow }>("tasklist.note", asParams({ taskId, note }));
}

export function archiveTask(taskId: string): Promise<{ task: TaskRow }> {
  return rpc().call<{ task: TaskRow }>("tasklist.archive", asParams({ taskId }));
}

export function reopenTask(taskId: string, note?: string): Promise<{ task: TaskRow }> {
  return rpc().call<{ task: TaskRow }>("tasklist.reopen", asParams({ taskId, note }));
}
